import { FeatureFlagsService } from 'apps/phoenix/src/app/shared/services';
import { PhoenixTrpcClient } from 'apps/phoenix/src/app/utils';
import { NEVER, Observable, Subject, from, takeUntil } from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  input,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { AutocompleteOption } from '../../interfaces';

type PartnerInfo = 'ALL' | 'PSCU' | 'NICE_ACTIMIZE' | 'DEFENSE_STORM';

@Component({
  selector: 'phoenix-client-autocomplete',
  templateUrl: './client-autocomplete.component.html',
  standalone: false,
})
export class ClientAutocompleteComponent implements OnInit, OnDestroy {
  phoenixTrpc = inject(PhoenixTrpcClient);
  featureFlagsService = inject(FeatureFlagsService);
  destroy$ = new Subject<void>();

  @Input() control = new FormControl('');
  @Input() partnerInfo = new FormControl('');
  @Input() entitlements: string[] = [];
  @Input() autoFocus = true;
  /**
   * Whether the picked client also becomes the app-wide feature-flag client.
   * Meant for client filters; turn off when the client is a value on a record.
   */
  @Input() syncFeatureFlags = true;
  @Output() clientChange = new EventEmitter<string>();
  /**
   * Whether the client held by `control` resolved to a real client. Emitted
   * only for `refresh`-driven lookups, and not at all when one fails.
   */
  @Output() isValidClient = new EventEmitter<boolean>();
  selectedPartnerInfo!: PartnerInfo;

  /**
   * Emit to resolve the client currently held by `control` into a displayable
   * option. Forwarded to the autocomplete, which does the lookup; see its
   * `refresh` input for the stable-reference and init-resolve semantics.
   */
  refresh = input<Observable<void>>(NEVER);

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value === '') {
          this.clientChanged('');
        }
      });

    if (this.isValidPartnerInfo(this.partnerInfo.value)) {
      this.selectedPartnerInfo = this.partnerInfo.value;
    }
  }

  isValidPartnerInfo(value: any): value is PartnerInfo {
    return ['ALL', 'PSCU', 'NICE_ACTIMIZE', 'DEFENSE_STORM'].includes(value);
  }

  clientChanged(client: string) {
    this.clientChange.emit(client);
    if (this.syncFeatureFlags) {
      void this.featureFlagsService.updateClientName(client || undefined);
    }
  }

  onValueResolved(resolved: boolean): void {
    this.isValidClient.emit(resolved);

    if (resolved && this.syncFeatureFlags) {
      void this.featureFlagsService.updateClientName(
        this.control.value || undefined
      );
    }
  }

  clientsAutocomplete = (val: string) => {
    return from(
      this.phoenixTrpc.client.autocomplete.query({
        search: val,
        partnerInfo: this.selectedPartnerInfo,
        entitlements: this.entitlements,
      })
    ).pipe(takeUntil(this.destroy$));
  };

  autocompleteFilter = (val: string | AutocompleteOption) => {
    if (typeof val === 'object') {
      return val.fullName + ' ' + `(${val.id})` + ' ' + val.state;
    }
    return val;
  };

  getOptionHtml(option: any): string {
    const stateClass =
      option.state === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-300';

    return `
      <div class="flex items-center gap-3">
        <span class="font-bold ">${option.fullName}</span>
        <span class="text-sm text-gray-500">(${option.id})</span>
        <span class="text-sm text-white ${stateClass} px-2 rounded ml-auto">${option.state}</span>
    </div>`;
  }

  sortOptions(data: { state: string; fullName: string }[]): any[] {
    return data.sort((a, b) => {
      const priority = {
        ACTIVE: 1,
        INACTIVE: 2,
        DELETED: 3,
      };

      const stateA = priority[a.state as keyof typeof priority] || Infinity;
      const stateB = priority[b.state as keyof typeof priority] || Infinity;

      if (stateA !== stateB) {
        return stateA - stateB;
      }

      return a.fullName.localeCompare(b.fullName);
    });
  }

  errorMessage = {
    required: 'This field is required',
  };

  onPartnerInfoChange(selectedPartner: PartnerInfo) {
    this.control.setValue('');
    this.clientChanged('');
    this.selectedPartnerInfo = selectedPartner;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
